import { Typography } from 'antd';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import {
  CHANNELS_FILTER,
  DELIVERY_PARTNER_FILTER,
  IS_VOUCHER,
  ORDER_TYPE,
  PAYMENT_STATUS,
  PAYMENT_STATUSES,
  SHIPPING_METHOD,
} from 'constants/constants';
import ButtonCompletePick from 'containers/Orders/components/ButtonCompletePick';
import RestList from 'containers/rest/List';
import { sumBy, upperFirst } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { formatDateTime, formatMoney } from 'utils/formattedString';
import { DATA_ORDERLIST_PICK_PACK_FIELD_SEARCH } from '../utils';
import useGetSearchKey from 'hooks/useSearchKeys';
import FilterComponent from 'containers/Orders/components/Filters/FilterComponent';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { OPERATORS_WITH_CONST } from 'constants/filters';
import { OrderStatusLabel } from 'components/commons/StatusLabel';

const OrderList = ({
  currentData,
  columns,
  initialFilter,
}: {
  currentData: DynamicObjectType;
  columns?: IRestColumnProps[];
  initialFilter?: DynamicObjectType;
}) => {
  const { t } = useTranslation();

  const { searchKeys } = useGetSearchKey(DATA_ORDERLIST_PICK_PACK_FIELD_SEARCH);

  const { searchParams } = useGetFilter();

  const restInitialFilter: FilterFromUrlResponseType = {
    join: ['orderBatch', 'orderBatch.batchItems'],
    filters: [
      {
        condition: 'and',
        key: 'orderBatchId',
        operator: 'eq',
        value: currentData?.id,
      },
      {
        condition: 'and',
        key: 'isPostWs',
        operator: 'is',
        value: false,
      },
      ...(initialFilter?.filters || []),
    ],
    filter: [initialFilter?.filter],
  };

  const _columns: IRestColumnProps[] = useMemo(() => {
    return (
      columns ?? [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                {d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
        },
        {
          name: 'statusOms',
          title: t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'wspId',
          title: t('orders.wspId'),
          isDefault: true,
          render: (d: string) => <p style={{ minWidth: 150 }}>{d || ''}</p>,
        },
        {
          name: 'saleOrderId',
          title: t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <p style={{ minWidth: 150 }}>{d || ''}</p>,
        },
        {
          name: 'paymentMethod',
          title: t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'customer',
          title: t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'items',
          title: t('orders.numberOfItems'),
          render: (d: DynamicObjectType[]) => (
            <p className="min-w-[120px]">{sumBy(d, 'quantity') || 0}</p>
          ),
          isDefault: true,
        },
        {
          name: 'total',
          title: t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
        },

        {
          name: 'channel',
          title: t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
          isDefault: true,
        },
      ]
    );
  }, [columns]);

  const filters: TFilterItem[] = [
    // {
    //   key: 'orders.statusOms',
    //   label: 'Order Status',
    //   dataSource: toArray(SELLER_ORDER_STATUS_CONST).map(
    //     (e: DynamicObjectType) => ({
    //       value: e.value,
    //       label: t(e.text),
    //     }),
    //   ),
    // },
    {
      key: 'voucher',
      label: 'Order with Voucher',
      dataSource: IS_VOUCHER,
    },
    {
      key: 'paymentStatus',
      label: 'Payment Status',
      dataSource: PAYMENT_STATUSES.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.text),
      })),
    },
    {
      key: 'paymentType',
      label: 'Payment Method',
      valueCPN: (
        <FilterComponent
          reference={{
            model: 'payment-method',
            valueProp: 'name',
            labelFormat: (e) => upperFirst(e?.name || ''),
            initFilters: {
              filters: [
                {
                  condition: 'and',
                  key: 'isActive',
                  operator: 'eq',
                  value: true,
                },
              ],
            },
          }}
        />
      ),
    },
    {
      key: 'createdAt',
      label: 'Order created time',
      isDatePicker: true,
    },
    // {
    //   key: 'cancelTime',
    //   label: 'Order cancelled time',
    //   isDatePicker: true,
    // },
    // {
    //   key: 'postInvoiceDate',
    //   label: 'Invoice creation time',
    //   isDatePicker: true,
    // },
    {
      key: 'orderType',
      label: 'Order Type',
      dataSource: ORDER_TYPE.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.text),
      })),
    },
    {
      key: 'channel',
      label: 'Channel',
      dataSource: CHANNELS_FILTER.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.label),
      })),
    },
    {
      key: 'shippingType',
      label: 'Delivery type',
      dataSource: SHIPPING_METHOD.map((e: DynamicObjectType) => ({
        value: e.key,
        label: t(e.text),
      })),
    },
    {
      key: 'orders.shippingCarrier.name',
      label: 'Delivery partner',
      dataSource: DELIVERY_PARTNER_FILTER,
      operators: OPERATORS_WITH_CONST,
    },
    {
      key: 'isGiftCards',
      label: 'Gift Cards',
      dataSource: IS_VOUCHER,
    },
    // {
    //   key: 'sipNumber',
    //   label: 'SIP number',
    //   dataSource: IS_VOUCHER,
    // },
    {
      key: 'locationId',
      label: 'Warehouse',
      valueCPN: (
        <FilterComponent
          reference={{
            labelProp: 'name',
            model: 'locations',
            searchMoreKeys: ['id', 'name'],
            valueProp: 'id',
            labelFormat: (item) =>
              item?.name ? `${item?.name} | ${item?.code}` : item?.code,
            initFilters: {
              filters: [
                {
                  condition: 'and',
                  key: 'isOrderProcess',
                  operator: 'eq',
                  value: true,
                },
              ],
            },
          }}
        />
      ),
    },
    {
      key: 'note',
      label: 'Note',
      isCheckNull: true,
      dataSource: [
        {
          value: true,
          label: 'True',
        },
        {
          value: false,
          label: 'False',
        },
      ],
    },
  ];

  const extraActionsFilter = <ButtonCompletePick />;

  return (
    <RestList
      initialFilter={restInitialFilter}
      columns={_columns}
      model="ordersDetail"
      filters={filters}
      searchKeys={searchKeys}
      hasOnlyFilter
      placeholderInputSearch="Search by Order ID, SO, SKU, Shipping Phone, Email, ..."
      hasImport={false}
      hasExport={false}
      hasCustomLayout
      extraActionFilters={
        currentData?.batchStatus === 'PARTIALLY_COMPLETED' && extraActionsFilter
      }
      dataFieldSearch={DATA_ORDERLIST_PICK_PACK_FIELD_SEARCH}
      searchCondition={
        searchParams.get('fieldSearch') === 'orderNumber' ? 'and' : 'or'
      }
    />
  );
};

export default OrderList;
